﻿import { TCovenantsPageStore, TAsyncSlice, TThunkError, FetchStatus } from '../types.ts';
import type {
  TApiApplication,
  TApiApplicationDocument,
  TApiSupervisorContragentBlock,
} from '~/modules/covenants/types.ts';
import { StatusFilterValue } from '~/modules/covenants/types.ts';
import {
  buildApplicationDocumentRowKey,
  buildApplicationDocumentStableKey,
} from '~/modules/covenants/utils.ts';
import { initialState } from '../initialState.ts';

export const setPendingAction = <T>(slice: TAsyncSlice<T>) => {
  slice.status = FetchStatus.Loading;
  slice.error = null;
};

export const setFulfilledAction = <T>(slice: TAsyncSlice<T>, data: T) => {
  slice.status = FetchStatus.Success;
  slice.data = data;
  slice.error = null;
};

export const setRejectedAction = <T>(slice: TAsyncSlice<T>, error: unknown) => {
  slice.status = FetchStatus.Error;
  slice.error = (error as TThunkError) ?? { message: 'Помилка запиту' };
};

export const resetCurrentContragentAction = (state: TCovenantsPageStore) => {
  state.currentContragent = initialState.currentContragent;
  state.supervisorContragentBlocks = initialState.supervisorContragentBlocks;
  state.requiredDocuments = initialState.requiredDocuments;
  state.covenants = initialState.covenants;
  state.applications = initialState.applications;
  state.activeApplications = initialState.activeApplications;
};

export const resetCurrentApplicationAction = (state: TCovenantsPageStore) => {
  state.currentApplication = initialState.currentApplication;
  state.applicationDocuments = initialState.applicationDocuments;
  state.applicationUnsignedDocumentsCount = initialState.applicationUnsignedDocumentsCount;
  state.certificateGeneration = initialState.certificateGeneration;
};

export const resetCurrentSupervisorApplicationAction = (state: TCovenantsPageStore) => {
  state.currentSupervisorApplication = initialState.currentSupervisorApplication;
  state.supervisorApplicationDocuments = initialState.supervisorApplicationDocuments;
  state.supervisorReadyCovenants = initialState.supervisorReadyCovenants;
};

export const resetCovenantsAction = (state: TCovenantsPageStore) => {
  state.covenants = initialState.covenants;
};

const getSupervisorContragentBlocksAction = (
  state: TCovenantsPageStore
): TApiSupervisorContragentBlock[] => state.supervisorContragentBlocks.data.blocks;

export const removeSupervisorContragentBlockAction = (
  state: TCovenantsPageStore,
  email: string
) => {
  const currentBlocks = getSupervisorContragentBlocksAction(state);

  state.supervisorContragentBlocks.data.blocks = currentBlocks.filter(
    (block) => block.email !== email
  );
  state.supervisorContragentBlocks.data.pageConfiguration.totalElements = Math.max(
    0,
    state.supervisorContragentBlocks.data.pageConfiguration.totalElements - 1
  );
  state.supervisorContragentBlocks.error = null;

  if (state.supervisorContragentBlocks.status === FetchStatus.Idle) {
    state.supervisorContragentBlocks.status = FetchStatus.Success;
  }
};

export const addApplicationAction = (state: TCovenantsPageStore, application: TApiApplication) => {
  state.applications.data.applications.push(application);
  state.applications.data.count += 1;
  state.applications.data.pageConfiguration.totalElements += 1;
};

export const getApplicationDocumentsAction = (state: TCovenantsPageStore) =>
  state.applicationDocuments.data.applicationDocuments;

export const setApplicationDocumentsAction = (
  state: TCovenantsPageStore,
  documents: TApiApplicationDocument[]
) => {
  state.applicationDocuments.data.applicationDocuments = documents;
};

const getApplicationDocumentInsertIndex = ({
  nextDocuments,
  groupKey,
  existingIndex,
}: {
  nextDocuments: TApiApplicationDocument[];
  groupKey: string;
  existingIndex: number;
}) => {
  if (existingIndex >= 0) {
    return Math.min(existingIndex, nextDocuments.length);
  }

  const lastGroupIndex = nextDocuments.reduce(
    (index, document, currentIndex) =>
      buildApplicationDocumentStableKey(document) === groupKey ? currentIndex : index,
    -1
  );

  if (lastGroupIndex < 0) {
    return nextDocuments.length;
  }

  return lastGroupIndex + 1;
};

export const upsertApplicationDocumentAction = (
  state: TCovenantsPageStore,
  payload: TApiApplicationDocument,
  baseDocument?: TApiApplicationDocument
) => {
  const currentDocuments = getApplicationDocumentsAction(state);
  const rowKey = buildApplicationDocumentRowKey(payload);
  const previousRowKey = baseDocument ? buildApplicationDocumentRowKey(baseDocument) : null;
  const groupKey = buildApplicationDocumentStableKey(baseDocument ?? payload);
  const existingIndex = currentDocuments.findIndex(
    (document) =>
      (!!previousRowKey && buildApplicationDocumentRowKey(document) === previousRowKey) ||
      buildApplicationDocumentRowKey(document) === rowKey
  );
  const nextDocuments = currentDocuments.filter((document) => {
    const documentRowKey = buildApplicationDocumentRowKey(document);

    if (previousRowKey && documentRowKey === previousRowKey) {
      return false;
    }

    return documentRowKey !== rowKey;
  });
  const insertIndex = getApplicationDocumentInsertIndex({
    nextDocuments,
    groupKey,
    existingIndex,
  });

  nextDocuments.splice(insertIndex, 0, payload);

  setApplicationDocumentsAction(state, nextDocuments);
  state.applicationDocuments.error = null;

  if (state.applicationDocuments.status === FetchStatus.Idle) {
    state.applicationDocuments.status = FetchStatus.Success;
  }
};

const buildDeletedApplicationDocumentPlaceholder = (
  document: TApiApplicationDocument
): TApiApplicationDocument => ({
  ...document,
  id: undefined,
  rowId: undefined,
  isVirtualRow: false,
  fileName: undefined,
  comment: undefined,
  createdAt: undefined,
  isCustomSignFile: false,
  hasSignedFile: false,
  status: StatusFilterValue.New,
});

export const removeDeletedApplicationDocumentAction = (
  state: TCovenantsPageStore,
  deletedDocument: TApiApplicationDocument
) => {
  const currentDocuments = getApplicationDocumentsAction(state);
  const previousRowKey = buildApplicationDocumentRowKey(deletedDocument);
  const groupKey = buildApplicationDocumentStableKey(deletedDocument);
  const groupDocuments = currentDocuments.filter(
    (document) => buildApplicationDocumentStableKey(document) === groupKey
  );
  const materializedGroupDocuments = groupDocuments.filter(
    (document) => !document.isVirtualRow && !!document.fileName
  );

  if (materializedGroupDocuments.length <= 1) {
    setApplicationDocumentsAction(
      state,
      currentDocuments.map((document) =>
        buildApplicationDocumentRowKey(document) === previousRowKey
          ? buildDeletedApplicationDocumentPlaceholder(document)
          : document
      )
    );
    return;
  }

  setApplicationDocumentsAction(
    state,
    currentDocuments.filter(
      (document) => buildApplicationDocumentRowKey(document) !== previousRowKey
    )
  );
};
